import React, { useEffect, useState, useRef, useMemo } from "react";
import { Form, Button } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";


import { toast } from "react-toastify";
import JoditEditor from "jodit-react";
import { allAPiServicesCall } from "../../../services/apiServices";
import { getAuthConfig, getFormDataConfig } from "../../../services/apiUtils";
import { ImageCropper, TARGET_ASPECT_RATIO } from "../../../components/ImageCropper";
import { extractObjectId } from "../../../utils/utils";

function EditNewsPage() {
  const { newsId } = useParams();
  const navigate = useNavigate();
  const [newsDetails, setNewsDetails] = useState(null);
  const [updatedData, setUpdatedData] = useState({});
  const isGovernedArticle = Boolean(
    updatedData.workflowStatus || updatedData.evidenceRunId,
  );
  const [newImageFile, setNewImageFile] = useState(null);
  const [authImageFile, setauthImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [authimagePreview, setAuthImagePreview] = useState("");
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [taggedArtists, setTaggedArtists] = useState([]);
  const [artistSearch, setArtistSearch] = useState('');
  const [artistResults, setArtistResults] = useState([]);
  const [showArtistDropdown, setShowArtistDropdown] = useState(false);
  const [genres, setGenres] = useState([]);
  const [newGenreInput, setNewGenreInput] = useState('');
  const [addingGenre, setAddingGenre] = useState(false);
  const [genreError, setGenreError] = useState(null);
  const [genreSearchQuery, setGenreSearchQuery] = useState('');
  const [showGenreList, setShowGenreList] = useState(false);

  // Tags state
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [newTagInput, setNewTagInput] = useState('');
  const [newTagType, setNewTagType] = useState('topic');
  const [addingTag, setAddingTag] = useState(false);
  const [tagError, setTagError] = useState(null);
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const [showTagList, setShowTagList] = useState(false);

  // Video state
  const [videoLoading, setVideoLoading] = useState(false);
  const [newVideoFile, setNewVideoFile] = useState(null);

  const TAG_TYPES = [
    { value: 'genre', label: 'Genre' },
    { value: 'mood', label: 'Mood' },
    { value: 'topic', label: 'Topic' },
    { value: 'era', label: 'Era' },
    { value: 'custom', label: 'Custom' },
  ];

  // Image cropper state
  const [showCropper, setShowCropper] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [aspectWarning, setAspectWarning] = useState(null);
  const coverImageInputRef = useRef(null);

  const editor = useRef(null);

  useEffect(() => {
    // Fetch the news details by ID
    async function fetchNewsDetails() {
      try {
        const response = await allAPiServicesCall.getNewsDetails({ newsId },
          getAuthConfig());
        setNewsDetails(response.data);
        setUpdatedData(response.data);
        setSelectedGenres(
          response.data.genres?.length
            ? response.data.genres
            : response.data.genre
              ? [response.data.genre]
              : [],
        );
        setTaggedArtists(response.data.taggedArtists || []);
        // Set tags - convert ObjectId format to string if needed
        setSelectedTags((response.data.tags || []).map(t => typeof t === 'string' ? t : extractObjectId(t)));
      } catch (error) {
        console.error("Error fetching news details:", error.response?.data || error);
      }
    }
    fetchNewsDetails();
  }, [newsId]);

  const fetchGenres = async () => {
    try {
      const response = await allAPiServicesCall.getGenres({}, getAuthConfig());
      // Sort genres alphabetically by genreName
      const sortedGenres = (response.data || []).sort((a, b) =>
        a.genreName.toLowerCase().localeCompare(b.genreName.toLowerCase())
      );
      setGenres(sortedGenres);
    } catch (error) {
      console.error('Error fetching genres:', error);
    }
  };

  useEffect(() => {
    fetchGenres();
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      const response = await allAPiServicesCall.getTags({}, getAuthConfig());
      if (response?.data?.tags) {
        setTags(response.data.tags);
      }
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const handleTagChange = (tagName) => {
    setSelectedTags(prev => {
      if (prev.includes(tagName)) {
        return prev.filter(name => name !== tagName);
      } else {
        return [...prev, tagName];
      }
    });
  };

  const handleAddNewTag = async () => {
    const trimmedName = newTagInput.trim();
    if (!trimmedName) return;

    // Check if tag already exists
    const existingTag = tags.find(t => t.name.toLowerCase() === trimmedName.toLowerCase());
    if (existingTag) {
      // Auto-select the existing tag instead of showing error
      if (!selectedTags.includes(existingTag.name)) {
        setSelectedTags(prev => [...prev, existingTag.name]);
        toast.success(`"${existingTag.name}" selected`);
      } else {
        toast.info(`"${existingTag.name}" is already selected`);
      }
      setNewTagInput('');
      setTagSearchQuery('');
      setTagError(null);
      return;
    }

    setAddingTag(true);
    try {
      const response = await allAPiServicesCall.createTag({
        name: trimmedName,
        type: newTagType,
        isActive: true
      }, getAuthConfig());
      if (response?.success) {
        // Refresh tags list
        await fetchTags();
        // Auto-select the new tag by name
        if (response.data?.name) {
          setSelectedTags(prev => [...prev, response.data.name]);
          toast.success(`"${response.data.name}" created and selected`);
        }
        setNewTagInput('');
        setTagSearchQuery('');
        setTagError(null);
      }
    } catch (error) {
      console.error('Error adding tag:', error);
      setTagError('Failed to add tag');
    } finally {
      setAddingTag(false);
    }
  };

  const handleAddNewGenre = async () => {
    const trimmedGenre = newGenreInput.trim();
    if (!trimmedGenre) return;

    // Check if genre already exists
    const existingGenre = genres.find(g => g.genreName.toLowerCase() === trimmedGenre.toLowerCase());
    if (existingGenre) {
      // Auto-select the existing genre instead of showing error
      if (!selectedGenres.includes(existingGenre.genreName)) {
        setSelectedGenres(prev => [...prev, existingGenre.genreName]);
        toast.success(`"${existingGenre.genreName}" selected`);
      } else {
        toast.info(`"${existingGenre.genreName}" is already selected`);
      }
      setNewGenreInput('');
      setGenreSearchQuery('');
      return;
    }

    setAddingGenre(true);
    try {
      const response = await allAPiServicesCall.addGenres([trimmedGenre], getAuthConfig());
      if (response?.success) {
        // Refresh genres list
        await fetchGenres();
        // Auto-select the new genre
        setSelectedGenres(prev => [...prev, trimmedGenre]);
        setNewGenreInput('');
        setGenreSearchQuery('');
        setGenreError(null);
        toast.success(`"${trimmedGenre}" created and selected`);
      }
    } catch (error) {
      console.error('Error adding genre:', error);
    } finally {
      setAddingGenre(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUpdatedData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Check image aspect ratio
  const checkImageAspectRatio = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const aspectRatio = img.width / img.height;
        const isGoodAspect = aspectRatio >= 1.5 && aspectRatio <= 2.1;
        resolve({ width: img.width, height: img.height, aspectRatio, isGoodAspect });
      };
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const { width, height, aspectRatio, isGoodAspect } = await checkImageAspectRatio(file);

    if (!isGoodAspect) {
      setAspectWarning(`Your image has aspect ratio ${aspectRatio.toFixed(2)}:1 (${width}x${height}). Recommended is ~1.82:1 (landscape). Please crop to avoid distortion.`);
      const reader = new FileReader();
      reader.onload = () => {
        setCropImageSrc(reader.result);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    } else {
      setAspectWarning(null);
      setNewImageFile(file);
      const objectUrl = URL.createObjectURL(file);
      setImagePreview(objectUrl);
    }
  };

  const handleCropComplete = (croppedFile) => {
    setAspectWarning(null);
    setCropImageSrc(null);
    setNewImageFile(croppedFile);
    const objectUrl = URL.createObjectURL(croppedFile);
    setImagePreview(objectUrl);
    if (coverImageInputRef.current) {
      coverImageInputRef.current.value = '';
    }
  };

  const uploadImageAndGetUrl = async () => {

    if (!newImageFile) return null;
    try {
      const formData = new FormData();
      formData.append("image", newImageFile);


      const response = await allAPiServicesCall.uploadNewsImage(
        formData,
        getFormDataConfig()
      );
      return response?.data?.path;
    } catch (error) {
      console.error("Image upload failed:", error.response?.data || error);
      return null;
    }
  };


  const uploadAuthorImageAndGetUrl = async (file) => {
    if (!authImageFile) return null;
    try {
      const formData = new FormData();
      formData.append("image", authImageFile);

      const response = await allAPiServicesCall.uploadAuthorImage(
        formData,
        // getFormDataConfig()
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('auth-token')}`,
            "Content-Type": "multipart/form-data",
          },
        },  
      );
      return response.data.path;
    } catch (error) {
      console.error("Author image upload failed:", error.response?.data || error);
      return null;
    }
  };


  const handleAuthorFileChange = (e) => {
    const file = e.target.files[0];
    setauthImageFile(file);
    const objectUrl = URL.createObjectURL(file);
    setAuthImagePreview(objectUrl);
  };

  const handleMultipleGenreChange = (genreName) => {
    setSelectedGenres(prev => {
      if (prev.includes(genreName)) {
        return prev.filter(g => g !== genreName);
      } else {
        return [...prev, genreName];
      }
    });
  };

  const handleArtistSearch = async (searchTerm) => {
    setArtistSearch(searchTerm);
    if (searchTerm.length < 2) {
      setArtistResults([]);
      setShowArtistDropdown(false);
      return;
    }

    try {
      const response = await allAPiServicesCall.getSearchArtist(
        { query: searchTerm },
        getAuthConfig(),
        navigate
      );
      if (response?.data) {
        setArtistResults(response.data);
        setShowArtistDropdown(true);
      }
    } catch (error) {
      console.error('Error searching artists:', error);
    }
  };

  const handleAddArtist = (artist) => {
    if (!taggedArtists.find(a => extractObjectId(a._id) === extractObjectId(artist._id))) {
      setTaggedArtists([...taggedArtists, artist]);
    }
    setArtistSearch('');
    setArtistResults([]);
    setShowArtistDropdown(false);
  };

  const handleRemoveArtist = (artistId) => {
    setTaggedArtists(taggedArtists.filter(a => extractObjectId(a._id) !== extractObjectId(artistId)));
  };

  const handleVideoChange = async (e) => {
    const videoFile = e.target.files[0];
    if (!videoFile) return;

    // Check file size (100MB limit)
    if (videoFile.size > 100 * 1024 * 1024) {
      toast.error('Video size should not exceed 100 MB.');
      return;
    }

    // Validate file type
    const validTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo', 'video/mpeg'];
    if (!validTypes.includes(videoFile.type)) {
      toast.error('Invalid video format. Allowed formats: MP4, WebM, MOV, AVI, MPEG');
      return;
    }

    setVideoLoading(true);
    try {
      const formData = new FormData();
      formData.append('video', videoFile);

      const response = await allAPiServicesCall.uploadNewsVideo(formData, getFormDataConfig(), navigate);
      setUpdatedData(prev => ({ ...prev, videoUrl: response.data?.path }));
      toast.success('Video uploaded successfully');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to upload video.');
    } finally {
      setVideoLoading(false);
    }
  };

  const handleRemoveVideo = () => {
    setUpdatedData(prev => ({ ...prev, videoUrl: '' }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    let imageUrl = updatedData.coverImg;

    if (newImageFile) {
      const uploadedImageUrl = await uploadImageAndGetUrl();
      if (uploadedImageUrl) {
        imageUrl = uploadedImageUrl;
      }
    }

    let authImageUrl = updatedData.authorPic;

    if (authImageFile) {
      const uploadedAuthorImageUrl = await uploadAuthorImageAndGetUrl();
      if (uploadedAuthorImageUrl) {
        authImageUrl = uploadedAuthorImageUrl;// Set the uploaded URL to authorPic
      }
    }

    const finalUpdatedData = {
      ...updatedData,
      coverImg: imageUrl,
      newsId,
      authorPic: authImageUrl,
      genre: selectedGenres[0] || updatedData.genre || undefined,
      genres: selectedGenres,
      tags: selectedTags,
      taggedArtists: taggedArtists.map(artist => extractObjectId(artist._id)),
      isHeroBanner: updatedData.isHeroBanner,
      primaryTag: updatedData.primaryTag || 'Music News'
    };

    try {
      const updateResponse = await allAPiServicesCall.putUpdateNews(
        finalUpdatedData,
        getAuthConfig()
      );
      console.log(updateResponse)
      if (updateResponse.data && updateResponse.status) {
        toast.success("News updated successfully");
        navigate("/news"); // Redirect to the news list page
      } else {
        toast.error("Failed to update news");
      }
    } catch (error) {
      console.error("Failed to update news:", error.response?.data || error);
      toast.error("An error occurred while updating the news");
    }
  };



  if (!newsDetails) return <div>Loading...</div>;

  return (
    <div className="edit-news-page container mt-4">
      <h2>Edit News</h2>
      <Form onSubmit={onSubmit}>
        <Form.Group className="mb-3">
          <Form.Label>Genres:</Form.Label>

          {/* Search/Add Genre Input */}
          <div className="input-group mb-2">
            <Form.Control
              type="text"
              placeholder="Search or add genre..."
              value={genreSearchQuery || newGenreInput}
              onChange={(e) => {
                setGenreSearchQuery(e.target.value);
                setNewGenreInput(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddNewGenre();
                }
              }}
              disabled={addingGenre}
            />
            <Button
              variant="outline-primary"
              onClick={handleAddNewGenre}
              disabled={addingGenre || !newGenreInput.trim()}
            >
              {addingGenre ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              ) : (
                'Add Genre'
              )}
            </Button>
          </div>
          <Form.Text className="text-muted d-block mb-2">Type to search existing genres or add a new one. Press Enter or click Add to select/create.</Form.Text>
          {genreError && <small className="text-danger d-block mb-2">{genreError}</small>}

          {/* Selected Genres Display */}
          {selectedGenres.length > 0 && (
            <div className="mb-2">
              <div className="d-flex flex-wrap gap-1">
                {selectedGenres.map((genre) => (
                  <span key={genre} className="badge bg-primary d-flex align-items-center gap-1">
                    {genre}
                    <button
                      type="button"
                      className="btn-close btn-close-white"
                      style={{ fontSize: '0.5rem' }}
                      onClick={() => handleMultipleGenreChange(genre)}
                      aria-label="Remove"
                    ></button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Toggle to show/hide genre list */}
          <button
            type="button"
            className="btn btn-link btn-sm p-0 text-decoration-none"
            onClick={() => setShowGenreList(!showGenreList)}
          >
            {showGenreList ? '▼ Hide genre list' : '▶ Browse all genres'}
          </button>

          {/* Collapsible Genre List */}
          {showGenreList && (
            <div className="border rounded p-3 mt-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {genres
                .filter(genre =>
                  !genreSearchQuery ||
                  genre.genreName.toLowerCase().includes(genreSearchQuery.toLowerCase())
                )
                .map((genre) => (
                  <div key={extractObjectId(genre._id)} className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      value={genre.genreName}
                      id={`edit-genre-${extractObjectId(genre._id)}`}
                      checked={selectedGenres.includes(genre.genreName)}
                      onChange={() => handleMultipleGenreChange(genre.genreName)}
                    />
                    <label className="form-check-label" htmlFor={`edit-genre-${extractObjectId(genre._id)}`}>
                      {genre.genreName}
                    </label>
                  </div>
                ))}
              {genres.filter(genre =>
                !genreSearchQuery ||
                genre.genreName.toLowerCase().includes(genreSearchQuery.toLowerCase())
              ).length === 0 && (
                <p className="text-muted mb-0">No genres found. Click "Add Genre" to create "{genreSearchQuery}"</p>
              )}
            </div>
          )}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Tag Artists:</Form.Label>
          <div className="position-relative">
            <Form.Control
              type="text"
              value={artistSearch}
              onChange={(e) => handleArtistSearch(e.target.value)}
              placeholder="Search for artists to tag..."
            />
            {showArtistDropdown && artistResults.length > 0 && (
              <div className="position-absolute w-100 bg-white border rounded shadow-sm mt-1" style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
                {artistResults.map((artist) => (
                  <div
                    key={extractObjectId(artist._id)}
                    className="p-2"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleAddArtist(artist)}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                  >
                    {artist.artist_name}
                  </div>
                ))}
              </div>
            )}
          </div>
          {taggedArtists.length > 0 && (
            <div className="mt-2">
              <label className="form-label">Tagged Artists:</label>
              <div className="d-flex flex-wrap gap-2">
                {taggedArtists.map((artist) => (
                  <span key={extractObjectId(artist._id) || artist.artist_id} className="badge bg-primary d-flex align-items-center gap-1">
                    {artist.artist_name}
                    <button
                      type="button"
                      className="btn-close btn-close-white"
                      style={{ fontSize: '0.6rem' }}
                      onClick={() => handleRemoveArtist(extractObjectId(artist._id) || artist.artist_id)}
                      aria-label="Remove"
                    ></button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </Form.Group>

        {/* Tags Section */}
        <Form.Group className="mb-3">
          <Form.Label>Tags:</Form.Label>

          {/* Search/Add Tag Input */}
          <div className="row g-2 mb-2">
            <div className="col-md-6">
              <Form.Control
                type="text"
                placeholder="Search or add tag..."
                value={tagSearchQuery || newTagInput}
                onChange={(e) => {
                  setTagSearchQuery(e.target.value);
                  setNewTagInput(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddNewTag();
                  }
                }}
                disabled={addingTag}
              />
            </div>
            <div className="col-md-3">
              <Form.Select
                value={newTagType}
                onChange={(e) => setNewTagType(e.target.value)}
                disabled={addingTag}
              >
                {TAG_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </Form.Select>
            </div>
            <div className="col-md-3">
              <Button
                variant="outline-primary"
                className="w-100"
                onClick={handleAddNewTag}
                disabled={addingTag || !newTagInput.trim()}
              >
                {addingTag ? (
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                ) : (
                  'Add Tag'
                )}
              </Button>
            </div>
          </div>
          <Form.Text className="text-muted d-block mb-2">Type to search existing tags or add a new one. Select tag type for new tags.</Form.Text>
          {tagError && <small className="text-danger d-block mb-2">{tagError}</small>}

          {/* Selected Tags Display */}
          {selectedTags.length > 0 && (
            <div className="mb-2">
              <div className="d-flex flex-wrap gap-1">
                {selectedTags.map((tagName) => {
                  const tag = tags.find(t => t.name === tagName);
                  return (
                    <span
                      key={tagName}
                      className="badge d-flex align-items-center gap-1"
                      style={{ backgroundColor: tag?.color || '#6366f1' }}
                    >
                      {tagName}
                      <button
                        type="button"
                        className="btn-close btn-close-white"
                        style={{ fontSize: '0.5rem' }}
                        onClick={() => handleTagChange(tagName)}
                        aria-label="Remove"
                      ></button>
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Toggle to show/hide tag list */}
          <button
            type="button"
            className="btn btn-link btn-sm p-0 text-decoration-none"
            onClick={() => setShowTagList(!showTagList)}
          >
            {showTagList ? '▼ Hide tag list' : '▶ Browse all tags'}
          </button>

          {/* Collapsible Tag List */}
          {showTagList && (
            <div className="border rounded p-3 mt-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {tags.length === 0 ? (
                <p className="text-muted mb-0">No tags available. Use the input above to add one.</p>
              ) : (
                tags
                  .filter(tag => tag.isActive)
                  .filter(tag =>
                    !tagSearchQuery ||
                    tag.name.toLowerCase().includes(tagSearchQuery.toLowerCase())
                  )
                  .map((tag) => (
                    <div key={extractObjectId(tag._id)} className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        value={tag.name}
                        id={`edit-tag-${extractObjectId(tag._id)}`}
                        checked={selectedTags.includes(tag.name)}
                        onChange={() => handleTagChange(tag.name)}
                      />
                      <label className="form-check-label" htmlFor={`edit-tag-${extractObjectId(tag._id)}`}>
                        <span className="badge me-1" style={{ backgroundColor: tag.color || '#6366f1' }}>
                          {tag.type}
                        </span>
                        {tag.name}
                      </label>
                    </div>
                  ))
              )}
              {tags.filter(tag => tag.isActive).filter(tag =>
                !tagSearchQuery ||
                tag.name.toLowerCase().includes(tagSearchQuery.toLowerCase())
              ).length === 0 && tags.length > 0 && (
                <p className="text-muted mb-0">No tags found. Click "Add Tag" to create "{tagSearchQuery}"</p>
              )}
            </div>
          )}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Title</Form.Label>
          <Form.Control
            type="text"
            name="title"
            value={updatedData.title}
            onChange={handleInputChange}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Author</Form.Label>
          <Form.Control
            type="text"
            name="author"
            value={updatedData.author}
            onChange={handleInputChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Description</Form.Label>
          <JoditEditor
            ref={editor}
            name="description"
            value={updatedData.description}
            onChange={(newContent) =>
              setUpdatedData((prevData) => ({
                ...prevData,
                description: newContent,
              }))
            }
          />
        </Form.Group>

        {/* Additional Fields */}
        <Form.Group className="mb-3">
          <Form.Label>Read Time</Form.Label>
          <Form.Control
            type="text"
            name="readTime"
            value={updatedData.readTime}
            onChange={handleInputChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Instagram</Form.Label>
          <Form.Control
            type="text"
            name="instagram"
            value={updatedData.instagram}
            onChange={handleInputChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Author Picture</Form.Label>
          {updatedData.authorPic && (
            <div className="mb-3 text-center">
              <img
                src={authimagePreview || updatedData.authorPic}
                alt="Author"
                style={{ height: "200px", objectFit: "cover" }}
              />
            </div>
          )}
          <Form.Control
            type="file"
            name="authorPic"
            onChange={handleAuthorFileChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Author Designation</Form.Label>
          <Form.Control
            type="text"
            name="authorDesignation"
            value={updatedData.authorDesignation}
            onChange={handleInputChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>News Image</Form.Label>
          <div className="alert alert-info py-2 mb-2" style={{ fontSize: '0.85rem' }}>
            <strong>Image Guidelines:</strong> Use landscape images with aspect ratio ~1.82:1 (e.g., 1024x563 pixels).
            Square or portrait images will be cropped.
          </div>
          {aspectWarning && (
            <div className="alert alert-warning py-2 mb-2" style={{ fontSize: '0.85rem' }}>
              {aspectWarning}
            </div>
          )}
          {(imagePreview || updatedData.coverImg) && (
            <div className="mb-3">
              <p className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>Preview (as it will appear on the website):</p>
              <div style={{
                width: '100%',
                maxWidth: '400px',
                aspectRatio: '1.82',
                overflow: 'hidden',
                borderRadius: '8px',
                border: '2px solid #dee2e6'
              }}>
                <img
                  src={imagePreview || updatedData.coverImg}
                  alt="News"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
            </div>
          )}
          <Form.Control
            type="file"
            name="coverImg"
            ref={coverImageInputRef}
            onChange={handleFileChange}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>News Image Credits</Form.Label>
          <Form.Control
            type="text"
            name="coverImgCreds"
            value={updatedData.coverImgCreds}
            onChange={handleInputChange}
          />
        </Form.Group>

        {!isGovernedArticle && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>
                Image Rights Status{" "}
                <small className="text-muted">(optional for manual publishing)</small>
              </Form.Label>
              <Form.Select
                name="coverImgRightsStatus"
                value={updatedData.coverImgRightsStatus || ""}
                onChange={handleInputChange}
              >
                <option value="">-- Select rights status --</option>
                <option value="verified">Verified</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Image Rights Holder{" "}
                <small className="text-muted">(optional for manual publishing)</small>
              </Form.Label>
              <Form.Control
                type="text"
                name="coverImgRightsHolder"
                value={updatedData.coverImgRightsHolder || ""}
                onChange={handleInputChange}
                maxLength={200}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Rights Evidence URL{" "}
                <small className="text-muted">(optional for manual publishing)</small>
              </Form.Label>
              <Form.Control
                type="url"
                name="coverImgRightsEvidenceRef"
                value={updatedData.coverImgRightsEvidenceRef || ""}
                onChange={handleInputChange}
                maxLength={500}
              />
            </Form.Group>
          </>
        )}

        <Form.Group className="mb-3">
          <Form.Label>Image Alt Text <small className="text-muted">(for accessibility & SEO)</small></Form.Label>
          <Form.Control
            type="text"
            name="imageAlt"
            value={updatedData.imageAlt || ''}
            onChange={handleInputChange}
            placeholder="Describe the image for screen readers..."
          />
          <Form.Text className="text-muted">
            Provide a brief description of the image content
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Image Caption <small className="text-muted">(optional)</small></Form.Label>
          <Form.Control
            type="text"
            name="imageCaption"
            value={updatedData.imageCaption || ''}
            onChange={handleInputChange}
            placeholder="Optional caption to display below the image..."
          />
        </Form.Group>

        {/* Video Upload Section */}
        <Form.Group className="mb-3">
          <Form.Label>Video <small className="text-muted">(optional - MP4, WebM, MOV, max 100MB)</small></Form.Label>
          <Form.Control
            type="file"
            onChange={handleVideoChange}
            accept="video/mp4,video/webm,video/quicktime,video/x-msvideo,video/mpeg"
            disabled={videoLoading}
          />
          {videoLoading && (
            <div className="d-flex justify-content-center mt-2">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Uploading video...</span>
              </div>
              <span className="ms-2">Uploading video...</span>
            </div>
          )}
          {updatedData.videoUrl && (
            <div className="mt-3">
              <p className="text-success mb-2">Video uploaded</p>
              <video
                src={updatedData.videoUrl}
                controls
                style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px' }}
              />
              <div className="mt-2">
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleRemoveVideo}
                >
                  Remove Video
                </Button>
              </div>
            </div>
          )}
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Publish Date</Form.Label>
          <Form.Control
            type="date"
            name="publishDate"
            value={updatedData.publishDate}
            onChange={handleInputChange}
          />
        </Form.Group>

        <Form.Group className="mb-4">
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="heroBannerSwitchEdit"
              checked={updatedData.isHeroBanner || false}
              onChange={(e) => setUpdatedData({...updatedData, isHeroBanner: e.target.checked})}
            />
            <label className="form-check-label" htmlFor="heroBannerSwitchEdit">
              <strong>Set as Homepage Main Article</strong>
              <small className="d-block text-muted">Feature this article on the homepage. Use Page Management to clear or fine-tune the homepage banner later.</small>
            </label>
          </div>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Primary Tag <small className="text-muted">(displayed on news card)</small></Form.Label>
          <Form.Control
            type="text"
            name="primaryTag"
            value={updatedData.primaryTag || 'Music News'}
            onChange={handleInputChange}
            placeholder="Music News"
          />
          <Form.Text className="text-muted">
            This tag will be displayed as a badge on the news card image. Default: "Music News"
          </Form.Text>
        </Form.Group>

        <Button variant="primary" type="submit" className="float-end">
          Save Changes
        </Button>
        <Button variant="secondary" onClick={() => navigate("/news")}>
          Cancel
        </Button>
      </Form>

      {/* Image Cropper Modal */}
      <ImageCropper
        show={showCropper}
        onHide={() => {
          setShowCropper(false);
          setCropImageSrc(null);
          if (coverImageInputRef.current) {
            coverImageInputRef.current.value = '';
          }
        }}
        imageSrc={cropImageSrc}
        onCropComplete={handleCropComplete}
        aspectRatio={TARGET_ASPECT_RATIO}
        title="Crop Cover Image"
      />
    </div>
  );
}

export default EditNewsPage;
